import axiosInstance from '../../api';
import API_ENDPOINTS from '../../api/endpoints';
import useGeneralStore from '../../../Store/generalStore';
import useFilterStore from '../../../Store/filterStore';
import useCountryStore from '../../../Store/countryStore';
import { parseApiResponse, extractData, isSuccessResponse, getErrorMessage } from '../../api/responseModel';
import { notifyApiError } from '../../api/errorUtils';
import { getRentalDays } from '../../utils/dateUtils';

const normalizeSearchVehicleResponse = (data) => {
    if (!data) {
        return { vehicles: [], rentalDays: null };
    }

    let vehicles = [];
    let rentalDays = null;

    if (Array.isArray(data)) {
        vehicles = data;
        rentalDays = getRentalDays(data[0]);
    } else if (typeof data === 'object') {
        vehicles = data.vehicles ?? data.Vehicles ?? [];
        rentalDays = getRentalDays(data) ?? getRentalDays(vehicles[0]);
        if (!Array.isArray(vehicles)) {
            vehicles = [];
        }
    }

    if (rentalDays != null) {
        vehicles = vehicles.map((vehicle) => ({
            ...vehicle,
            rentalDays: getRentalDays(vehicle) ?? rentalDays,
        }));
    }

    return { vehicles, rentalDays };
};

const useVehicle = () => {
    const { setLoading, setMessage, setError } = useGeneralStore();

    /**
     * Search available vehicles
     * @param {Object} searchParams - { fromAddress, toAddress, fromDateTime, toDateTime, countryCode, sipCode, siteCode, sourceTag?, pageNumber, pageSize, filters }
     * sourceTag is set when the user arrives via the encrypted partner URL (maps from JSON SourceTag).
     * filters shape: { fleetTypes?, priceFrom?, priceTo?, franchises?, fuelPolicy? }
     * @returns {Promise<{ vehicles: Array, rentalDays: number|null }>} Search results with rental days from API
     */
    const searchVehicles = async (searchParams) => {
        try {
            setLoading(true);
            
            // Partner deeplink: use countryCode from partner data as-is (null stays null).
            // Otherwise prefer explicit country from search params, fallback to selected country in store.
            const countryCode = searchParams.isPartnerFlow
                ? (searchParams.countryCode ?? null)
                : (searchParams.countryCode ?? useCountryStore.getState().getCountryCode());
            
            // Prepare request body - always include all required fields
            const requestBody = {
                fromAddress: searchParams.fromAddress || '',
                toAddress: searchParams.toAddress || searchParams.fromAddress || '',
                fromDateTime: searchParams.fromDateTime,
                toDateTime: searchParams.toDateTime,
                countryCode: countryCode || null, // nullable - can be null if no country selected
                sipCode: searchParams.sipCode ?? null, // nullable
                siteCode: searchParams.siteCode ?? null, // nullable
                sourceTag: searchParams.sourceTag ?? null, // nullable
                redirectId: searchParams.crxRedirectId ?? null, // nullable - crx_redirect_id from partner link
                companyId: searchParams.crxCompanyId ?? null, // nullable - crx_company_id from partner link
                pageNumber: searchParams.pageNumber || 1,
                pageSize: searchParams.pageSize || 10,
                filters: searchParams.filters !== undefined ? searchParams.filters : null, // null if no filters, not undefined
            };

            if (searchParams.isPartnerFlow) {
                requestBody.requestToken = searchParams.requestToken ?? null;
                requestBody.referer = searchParams.referer ?? null;
                requestBody.fingerprint = searchParams.fingerprint ?? null;
            }

            const response = await axiosInstance.post(API_ENDPOINTS.VEHICLE.SEARCH_AVAILABLE, requestBody);
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse);
                const { vehicles, rentalDays } = normalizeSearchVehicleResponse(data);
                setMessage(apiResponse.responseMessage || 'Vehicles found successfully');
                return { vehicles, rentalDays };
            }

            const errorMsg = getErrorMessage(apiResponse) || 'No vehicles found. Please try different search criteria.';
            setError(errorMsg);
            return { vehicles: [], rentalDays: null };
        } catch (error) {
            notifyApiError(setError, error, 'Failed to search vehicles.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Get filters for vehicle search
     * @param {Boolean} forceRefresh - Force refresh even if cached
     * @returns {Promise} Response data with filters (fleetTypes, franchises, fuelPolicies)
     */
    const getFilters = async (forceRefresh = false) => {
        try {
            // Check cache first
            const cachedFilters = useFilterStore.getState().filters;
            
            if (!forceRefresh && cachedFilters) {
                return cachedFilters;
            }

            // Don't set global loading for filters fetch to avoid UI blocking
            const response = await axiosInstance.get(API_ENDPOINTS.VEHICLE.GET_FILTERS);
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse) || { fleetTypes: [], franchises: [], fuelPolicies: [] };
                
                // Cache the filters
                useFilterStore.getState().setFilters(data);
                
                return data;
            } else {
                // If API fails but we have cache, return cache
                if (cachedFilters) {
                    return cachedFilters;
                }
                console.error('Failed to fetch filters');
                return { fleetTypes: [], franchises: [], fuelPolicies: [] };
            }
        } catch (error) {
            // Handle axios errors silently for filters
            // If timeout or network error, try to return cached filters
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                const cachedFilters = useFilterStore.getState().filters;
                if (cachedFilters) {
                    return cachedFilters;
                }
            }
            console.error('Failed to fetch filters:', error);
            return { fleetTypes: [], franchises: [], fuelPolicies: [] };
        }
    };

    /**
     * Get featured vehicles
     * @returns {Promise} Response data with featured vehicles list
     */
    const getFeaturedVehicles = async () => {
        try {
            setLoading(true);
            
            const response = await axiosInstance.get(API_ENDPOINTS.VEHICLE.GET_FEATURED);
            
            // Parse generic response
            const apiResponse = parseApiResponse(response);
            
            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse);
                return data?.featured || [];
            } else {
                const errorMsg = getErrorMessage(apiResponse) || 'Failed to fetch featured vehicles.';
                setError(errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            notifyApiError(setError, error, 'Failed to fetch featured vehicles.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        searchVehicles,
        getFilters,
        getFeaturedVehicles,
    };
};

export default useVehicle;

